'use client';

import { createContext, type ReactNode, useContext, useMemo, useRef } from 'react';

/** Recebe a navegação pedida e decide quando (e se) seguir com ela. */
export type InterceptadorDeNavegacao = (prosseguir: () => void) => void;

interface NavegacaoProtegida {
  /** A tela com alterações não salvas registra o seu interceptador; `null` libera. */
  registrar: (interceptador: InterceptadorDeNavegacao | null) => void;
  /** O menu lateral chama isto em vez de navegar direto. */
  navegar: (prosseguir: () => void) => void;
}

const NavegacaoProtegidaContext = createContext<NavegacaoProtegida | null>(null);

/**
 * Ponte entre o menu lateral do admin-geral e a tela aberta: o menu e a página são irmãos no
 * layout, então sem isto o clique no menu trocava de rota direto e a tela com alterações não
 * salvas não tinha como pedir confirmação. Guarda um interceptador só (uma tela por vez).
 *
 * @example <NavegacaoProtegidaProvider><AdminSidebar /> {children}</NavegacaoProtegidaProvider>
 */
export function NavegacaoProtegidaProvider({ children }: { children: ReactNode }) {
  const interceptador = useRef<InterceptadorDeNavegacao | null>(null);
  const valor = useMemo<NavegacaoProtegida>(
    () => ({
      registrar: (novo) => {
        interceptador.current = novo;
      },
      navegar: (prosseguir) => {
        if (interceptador.current) interceptador.current(prosseguir);
        else prosseguir();
      },
    }),
    [],
  );

  return (
    <NavegacaoProtegidaContext.Provider value={valor}>
      {children}
    </NavegacaoProtegidaContext.Provider>
  );
}

/** `null` fora do provider (ex.: testes de hook): quem usa navega direto. */
export function useNavegacaoProtegida(): NavegacaoProtegida | null {
  return useContext(NavegacaoProtegidaContext);
}
